import create from 'zustand';
import { LoginFormData, User } from 'ts/types';
import { ErrorResponse } from 'ts/types/ApiResponse';
import { http, ls } from 'utils';
import toast from 'react-hot-toast';
import CONFIG from 'config/constants';

type UserStore = {
  isLoggedIn: boolean;
  isLoading: boolean;
  isError: boolean;
  error: ErrorResponse | null;
  user: User | null;
  signIn: (formData: LoginFormData) => Promise<boolean>;
  signOut: () => void;
  getUser: () => void;
  resetInactivityTimer: () => void;
};

interface LoginResponse {
  token: string;
  user: User;
}

const INACTIVITY_TIMEOUT = 30 * 60 * 1000;

const useAuthStore = create<UserStore>((set, get) => {
  let inactivityTimer: NodeJS.Timeout;

  const updateLastActivityTime = () => {
    localStorage.setItem('lastActivityTime', Date.now().toString());
  };

  const signOut = (): void => {
    ls.remove(CONFIG.LSKEY_TOKEN);
    ls.remove('selectedTrader');
    localStorage.removeItem('lastActivityTime');
    set({ isLoggedIn: false, user: null });
    if (inactivityTimer) {
      clearTimeout(inactivityTimer);
    }
  };

  const checkInactivityOnWake = () => {
    const lastActivityTime = localStorage.getItem('lastActivityTime');
    if (!lastActivityTime) return;

    const elapsed = Date.now() - parseInt(lastActivityTime, 10);
    if (elapsed >= INACTIVITY_TIMEOUT) {
      signOut();
      toast.error("Session timed out - log in to continue.");
    } else {
      startInactivityTimer();
    }
  };

  const startInactivityTimer = () => {
    updateLastActivityTime();

    if (inactivityTimer) {
      clearTimeout(inactivityTimer);
    }

    inactivityTimer = setTimeout(() => {
      signOut();
      toast.error("Session timed out - log in to continue.");
    }, INACTIVITY_TIMEOUT);
  };

  const resetInactivityTimer = () => {
    startInactivityTimer();
  };

  if (typeof window !== 'undefined' && ls.get(CONFIG.LSKEY_TOKEN)) {
    setTimeout(checkInactivityOnWake, 0);
  }

  return {
    isLoggedIn: !!ls.get(CONFIG.LSKEY_TOKEN),
    isLoading: !!ls.get(CONFIG.LSKEY_TOKEN),
    isError: false,
    error: null,
    user: null,

    signIn: async (formData): Promise<boolean> => {
      set({ isLoading: true });
      try {
        const { data } = await http.post<LoginResponse>(`${process.env.REACT_APP_API_HOST}/login`, formData);
        const { token, user } = data;

        ls.add(CONFIG.LSKEY_TOKEN, token);
        set({
          isLoggedIn: true,
          isLoading: false,
          isError: false,
          error: null,
          user,
        });

        startInactivityTimer();
        return true;
      } catch (error) {
        set({ isLoading: false, isError: true, error: error as ErrorResponse });
        return false;
      }
    },

    signOut,

    getUser: async (): Promise<void> => {
      try {
        const { data } = await http.get<User>(`${process.env.REACT_APP_API_HOST}/profile`);
        set({ isLoggedIn: true, isLoading: false, isError: false, user: data });
        startInactivityTimer();
      } catch (error) {
        set({ isLoading: false, isError: true, error: error as ErrorResponse });
      }
    },

    resetInactivityTimer,
  };
});

export default useAuthStore;